import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

// =============================================================================
// TYPES
// =============================================================================

export interface LinkedKB {
  id: string
  name: string
}

export interface Tagset {
  id: string
  name: string
  description: string
  status: 'UNSTART' | 'RUNNING' | 'DONE' | 'FAIL'
  tag_count: number
  source_type: 'upload' | 'genclair' | 'empty'
  source_kb_id: string | null
  source_kb_name: string | null
  source_run_id: string | null
  has_document: boolean
  linked_kb_count: number
  linked_kbs: LinkedKB[]
  is_ready: boolean
  created_at: string
  updated_at: string
}

export interface TagsetsListResponse {
  tagsets: Tagset[]
}

export interface PaginatedTagsetsResponse {
  items: Tagset[]
  total: number
  page: number
  page_size: number
}

export interface KBTagsetsResponse {
  linked_tagsets: Tagset[]
  available_tagsets: Tagset[]
  kb_needs_reparse: boolean
  reparse_reason: string
}

export interface TagPreview {
  description: string
  tags: string
}

export interface TagsetTag {
  id: string
  content: string
}

export interface PaginatedTagsetTagsResponse {
  items: TagsetTag[]
  total: number
  page: number
  page_size: number
}

// =============================================================================
// TENANT-LEVEL TAGSET HOOKS
// =============================================================================

// Query: List all tenant tagsets (paginated)
export function useTagsets(params?: { page?: number; pageSize?: number; search?: string }) {
  const { page = 1, pageSize = 10, search = '' } = params || {}
  return useQuery({
    queryKey: ['tagsets', page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get<PaginatedTagsetsResponse>('/knowledge/api/tagsets/', {
        params: { page, page_size: pageSize, search },
      })
      return response.data
    },
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.items?.some((t: Tagset) => t.status === 'RUNNING')
      return hasRunning ? 3000 : false
    },
  })
}

// Mutation: Bulk delete tagsets
export function useBulkDeleteTagsets() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await knowledgeApi.post('/knowledge/api/tagsets/bulk-delete/', { ids })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
      queryClient.invalidateQueries({ queryKey: ['kb-tagsets'] })
    },
  })
}

// Query: Get tagset detail
export function useTagsetDetail(tagsetId: string) {
  return useQuery({
    queryKey: ['tagset', tagsetId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/tagsets/${tagsetId}/`)
      return response.data.tagset as Tagset
    },
    enabled: !!tagsetId,
    refetchInterval: (query) => {
      const tagset = query.state.data as Tagset | undefined
      return tagset?.status === 'RUNNING' ? 3000 : false
    },
  })
}

// Query: Get tagset status (lightweight polling)
export function useTagsetStatus(tagsetId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['tagset-status', tagsetId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/tagsets/${tagsetId}/status/`)
      return response.data
    },
    enabled,
    refetchInterval: 3000,
  })
}

// Query: List tags (chunks) in a tagset (paginated)
export function useTagsetTags(tagsetId: string, params?: { page?: number; pageSize?: number; search?: string }) {
  const { page = 1, pageSize = 10, search = '' } = params || {}
  return useQuery({
    queryKey: ['tagset-tags', tagsetId, page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get<PaginatedTagsetTagsResponse>(
        `/knowledge/api/tagsets/${tagsetId}/tags/`,
        { params: { page, page_size: pageSize, search } }
      )
      return response.data
    },
    enabled: !!tagsetId,
  })
}

// Mutation: Create tagset
export function useCreateTagset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { name: string; description?: string; source_type?: string }) => {
      const response = await knowledgeApi.post('/knowledge/api/tagsets/create/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
    },
  })
}

// Mutation: Upload tag file
export function useUploadTagFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ tagsetId, file }: { tagsetId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await knowledgeApi.post(
        `/knowledge/api/tagsets/${tagsetId}/upload/`,
        formData
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
      queryClient.invalidateQueries({ queryKey: ['tagset', variables.tagsetId] })
    },
  })
}

// Mutation: Parse tagset
export function useParseTagset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tagsetId: string) => {
      const response = await knowledgeApi.post(`/knowledge/api/tagsets/${tagsetId}/parse/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
    },
  })
}

// Mutation: Delete tagset
export function useDeleteTagset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tagsetId: string) => {
      const response = await knowledgeApi.delete(`/knowledge/api/tagsets/${tagsetId}/delete/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
      queryClient.invalidateQueries({ queryKey: ['kb-tagsets'] })
    },
  })
}

// =============================================================================
// KB ↔ TAGSET LINKING HOOKS
// =============================================================================

// Mutation: Link tagset to KB
export function useLinkTagsetToKB() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ tagsetId, kbId }: { tagsetId: string; kbId: string }) => {
      const response = await knowledgeApi.post(
        `/knowledge/api/tagsets/${tagsetId}/link/`,
        { kb_id: kbId }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
      queryClient.invalidateQueries({ queryKey: ['kb-tagsets'] })
    },
  })
}

// Mutation: Unlink tagset from KB
export function useUnlinkTagsetFromKB() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ tagsetId, kbId }: { tagsetId: string; kbId: string }) => {
      const response = await knowledgeApi.post(
        `/knowledge/api/tagsets/${tagsetId}/unlink/`,
        { kb_id: kbId }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
      queryClient.invalidateQueries({ queryKey: ['kb-tagsets'] })
    },
  })
}

// Query: List tagsets for a specific KB (linked + available)
export function useTagsetsForKB(kbId: string) {
  return useQuery({
    queryKey: ['kb-tagsets', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get<KBTagsetsResponse>(`/knowledge/${kbId}/tagsets/`)
      return response.data
    },
    enabled: !!kbId,
  })
}

// =============================================================================
// GENCLAIR EXPORT HOOK
// =============================================================================

// Mutation: Export taxonomy run as tagset
export function useExportTaxonomyAsTagset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ kbId, runId, name, description }: {
      kbId: string
      runId: string
      name?: string
      description?: string
    }) => {
      const response = await knowledgeApi.post(
        `/knowledge/${kbId}/taxonomy/runs/${runId}/export-tagset/`,
        { name: name || '', description: description || '' }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagsets'] })
    },
  })
}

// =============================================================================
// KB REPARSE HOOK
// =============================================================================

// Mutation: Reparse all documents in a KB
export function useReparseAllDocuments(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await knowledgeApi.post(`/knowledge/${kbId}/reparse-all/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-tagsets', kbId] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
      queryClient.invalidateQueries({ queryKey: ['documents', kbId] })
    },
  })
}
